import {action, computed, _} from '@api'
import {AssortmentHeroCardStore, AssortmentRootStore, layoutSettingDescriptors, LayoutSettingsDescriptor, UNASSIGNED} from '@modules'
import {AssortmentConfig, DEFAULT_CATALOG_USER_SETTINGS, IDragDropElement, ui} from '@stores'
import {observable} from 'mobx'
import {AssortmentLayoutSettings, CatalogInstanceChildStore, CatalogStore} from './'

export class CatalogAssortmentsStore extends CatalogInstanceChildStore {
  get assortmentRootStores() {
    return this.catalog.componentStores.assortmentRoots
  }

  constructor(public catalog: CatalogStore) {
    super(catalog)
  }

  get assortments() {
    return this.catalog.catalogConfig.assortments ?? []
  }

  @computed get assortmentsById(): { [id: string]: AssortmentConfig } {
    return _.keyBy(this.assortments, (a, i) => a?._id ?? i)
  }

  /**
   * Hide widgets that are specific to catalog items but not assortments (LOD, card spacing, image source, etc)
   */
  @computed get hideCatalogSpecificWidgets() {
    const {selectedAssortment, assortmentRootStores} = this
    return selectedAssortment && assortmentRootStores.catalog?.isCollapsedAll
  }

  @computed get selectedAssortment() {
    const {catalog: {settings: {assortment}}, assortmentsById} = this
    return assortment?.id ? assortmentsById[assortment.id] : null
  }

  set selectedAssortment(v) {
    const {catalog: {settings}} = this
    settings.assortment.id = v?._id

    if (v) {
      settings.align = 'center'
    }
  }

  //@observable.ref root: AssortmentRootStore

  @computed get roots(): Array<AssortmentRootStore> {
    return _.values(this.catalog.componentStores.assortmentRoots)
  }

  @computed get isExpandedAll() {
    return this.roots.every(s => s.isExpandedAll)
  }

  @computed get isCollapsedAll() {
    return this.roots.every(s => s.isCollapsedAll)
  }

  @action expandAll = () => {
    return this.roots.forEach(v => v.expandAll())
  }

  @action collapseAll = () => {
    return this.roots.forEach(v => v.collapseAll())
  }

  get settings() { return this.catalog.settings.assortment }

  @action resetSettingsToDefault = () => {
    const {settings: {layout}, selectedAssortment} = this
      _.assign(layout, DEFAULT_CATALOG_USER_SETTINGS.assortment.layout, selectedAssortment?.layout)
  }

  @action resetSetting = (key: keyof AssortmentLayoutSettings) => {
    const {settings: {layout},selectedAssortment} = this,
      defaultValue = selectedAssortment.layout[key] ?? layoutSettingDescriptors[key].defaultValue

    layout[key] = defaultValue
    this._justResetSetting = true
    setTimeout(() => this._justResetSetting = false, 50)
  }

  @observable _justResetSetting = false

  @action onSetSetting_ = _.throttle((key: keyof AssortmentLayoutSettings, value: number) => {
    if (!this._justResetSetting) {
      this.onSetSetting(key, value)
    }
  }, 20)

  @action onSetSetting = (key: keyof AssortmentLayoutSettings, value: number) => {
    this.settings.layout[key] = value
  }

  @action onDragDropHeroCardToHeroCard = async (drag: IDragDropElement, drop: IDragDropElement, context, dataTransfer: DataTransfer) => {
    // console.log(`Reorder Hero Cards`, {drag, drop, context}) //drag: libraryItem // drop:catalogItem

    const {targetExtra} = drag, dragHero = targetExtra.hero as AssortmentHeroCardStore

    if (_.isNumber(dragHero.cardStore.previewReorderCardIndex)) {
      _.merge(dragHero.cardStore.overrides.orders[dragHero.data.key?.toString() ?? UNASSIGNED], {index: dragHero.cardStore.previewReorderCardIndex})
    }
  }

  @action onDragDropVerifyAllowed = (drag: IDragDropElement, drop: IDragDropElement) => {
    // console.log(`Verify Hero Cards operation is valid`, {drag, drop}) //drag: libraryItem // drop:catalogItem

    if (!drop) return {allowed: true}

    if (drop.target != 'assortmentHeroCard') return {allowed: false}

    const dragStore = drag.targetExtra.hero as AssortmentHeroCardStore,
      dropStore = drop.targetExtra.hero as AssortmentHeroCardStore

    return {allowed: dragStore.cardStore.card === dropStore.cardStore.card}
  }

  swatchImageFor = (item: any) => {
    const {selectedAssortment: {swatches}, catalog: {imageUrlFor}} = this
    let r = (swatches ?? []).flatMap(({dataMap}) => _.get(item, dataMap.images as string ?? 'images')).filter(_.identity)
    // console.log(r)

    // r = r.map(v => _.isString(v) ? {exId: r} : v)
    // console.log(r)
    return _.first(r)
  }

  swatchImageUrlFor = (item: any) => {

    const {swatchImageFor, catalog: {imageUrlFor}} = this,
      image = swatchImageFor(item)
    return image ? imageUrlFor(image) : null
  }

  @action saveOverridesForAssortment = async (body: AssortmentOverrideSerialized) => {
    const {es: {indexes, genericUpsert, updateDocument}} = this
    await genericUpsert({
      index: indexes.assortmentOverrides,
      body,
      _id: body.assortmentKey
    })
  }
}

export interface AssortmentOverrideSerialized {
  assortmentKey: string,
  workspace: string,
  overridesJson: string
}


// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogAssortmentsStore.ts